import React, { useState, useEffect, useCallback } from 'react';
import {
  Heartbeat, TrendUp, TrendDown, Shield, Lightning, Clock,
  ArrowsClockwise, CaretRight, WarningCircle, CheckCircle,
  Funnel, CaretDown, CircleNotch, Database, Robot
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const API = BACKEND_URL;

// ── Color tokens ──
const C = {
  green: '#10B981', red: '#F87171', blue: '#6366F1', amber: '#F59E0B',
  gray: '#64748B', teal: '#14B8A6', violet: '#8B5CF6', cyan: '#22D3EE',
};

const STATUS_COLORS = {
  completed: C.green, failed: C.red, running: C.blue,
  cancelled: C.gray, blocked: C.amber,
};

// ═══════════════════════════════════════════════════════════════
// MINI CHART COMPONENTS (pure CSS — no chart lib needed)
// ═══════════════════════════════════════════════════════════════

const BarChart = ({ data, maxVal, colorFn, labelKey = 'label', valueKey = 'value' }) => {
  const mx = maxVal || Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="space-y-1.5">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[11px] text-[var(--text-muted)] truncate min-w-[90px] max-w-[120px]">{d[labelKey]}</span>
          <div className="flex-1 h-4 bg-[var(--surface-3)] rounded-sm overflow-hidden">
            <div
              className="h-full rounded-sm transition-all duration-500"
              style={{ width: `${Math.max((d[valueKey] / mx) * 100, 2)}%`, background: colorFn ? colorFn(d, i) : C.blue }}
            />
          </div>
          <span className="text-[11px] font-mono text-[var(--text-secondary)] min-w-[32px] text-right">{d[valueKey]}</span>
        </div>
      ))}
    </div>
  );
};

const DonutChart = ({ segments, size = 80 }) => {
  const total = segments.reduce((s, d) => s + d.value, 0) || 1;
  let cum = 0;
  const r = size / 2 - 6;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth="10" />
      {segments.map((seg, i) => {
        const pct = seg.value / total;
        const offset = circumference * (1 - pct);
        const rotation = (cum / total) * 360 - 90;
        cum += seg.value;
        return (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
            stroke={seg.color} strokeWidth="10"
            strokeDasharray={`${circumference}`}
            strokeDashoffset={offset}
            transform={`rotate(${rotation} ${size/2} ${size/2})`}
            style={{ transition: 'all 0.5s' }}
          />
        );
      })}
      <text x={size/2} y={size/2} textAnchor="middle" dy=".35em"
        fill="var(--text-primary)" fontSize="13" fontWeight="600">{total}</text>
    </svg>
  );
};

const Sparkline = ({ data, height = 32, color = C.blue }) => {
  if (!data?.length) return null;
  const vals = data.map(d => d.total || 0);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals);
  const w = 200;
  const points = vals.map((v, i) => `${(i / (vals.length - 1 || 1)) * w},${height - ((v - min) / (max - min || 1)) * (height - 4) - 2}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════
// METRIC CARD
// ═══════════════════════════════════════════════════════════════

const MetricCard = ({ label, value, sub, icon: Icon, color = C.blue, testId }) => (
  <div className="p-4 rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)]" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon size={14} weight="fill" style={{ color }} />}
      <span className="text-[11px] text-[var(--text-muted)] uppercase tracking-wider">{label}</span>
    </div>
    <div className="text-2xl font-bold text-[var(--text-primary)]">{value}</div>
    {sub && <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{sub}</div>}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// SECTION CARD
// ═══════════════════════════════════════════════════════════════

const Section = ({ title, icon: Icon, children, testId }) => (
  <div className="rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)] overflow-hidden" data-testid={testId}>
    <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center gap-2">
      {Icon && <Icon size={14} weight="fill" className="text-[var(--text-muted)]" />}
      <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// TIME RANGE PICKER
// ═══════════════════════════════════════════════════════════════

const TIME_RANGES = [
  { value: '1h', label: '1H' },
  { value: '6h', label: '6H' },
  { value: '24h', label: '24H' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
];

const TimeRangePicker = ({ value, onChange }) => (
  <div className="flex gap-0.5 p-0.5 rounded-lg bg-[var(--surface-2)]" data-testid="time-range-picker">
    {TIME_RANGES.map(tr => (
      <button key={tr.value} onClick={() => onChange(tr.value)}
        data-testid={`time-range-${tr.value}`}
        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
          value === tr.value
            ? 'bg-[var(--brand-primary)] text-white'
            : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
        }`}
      >{tr.label}</button>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// RECENT ACTIVITY FEED
// ═══════════════════════════════════════════════════════════════

const ActivityFeed = ({ items }) => {
  if (!items?.length) return <p className="text-xs text-[var(--text-muted)] text-center py-4">No recent activity</p>;
  return (
    <div className="space-y-1" data-testid="activity-feed">
      {items.map((r, i) => {
        const statusColor = STATUS_COLORS[r.status] || C.gray;
        return (
          <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--surface-2)] transition-colors" data-testid={`activity-item-${i}`}>
            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: statusColor }} />
            <span className="text-xs font-medium text-[var(--text-primary)] truncate max-w-[140px]">{r.agent_name || r.agent_id}</span>
            <span className="text-[10px] text-[var(--text-muted)]">{r.trigger_source}</span>
            <span className="ml-auto text-[10px] font-mono" style={{ color: statusColor }}>{r.status}</span>
            {r.duration_ms != null && <span className="text-[10px] text-[var(--text-muted)]">{r.duration_ms}ms</span>}
            {r.governance_blocked && <Shield size={10} className="text-[#F59E0B]" weight="fill" />}
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════

export default function RunHealthDashboard() {
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [providers, setProviders] = useState(null);
  const [governance, setGovernance] = useState(null);
  const [routing, setRouting] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [recent, setRecent] = useState(null);
  const [alertSummary, setAlertSummary] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const opts = { credentials: 'include' };
    const qs = `time_range=${timeRange}`;

    try {
      const [ov, pv, gv, rv, tv, rc, al] = await Promise.all([
        fetch(`${API}/api/dashboard/run-health?${qs}`, opts).then(r => r.json()),
        fetch(`${API}/api/dashboard/run-health/providers?${qs}`, opts).then(r => r.json()),
        fetch(`${API}/api/dashboard/run-health/governance?${qs}`, opts).then(r => r.json()),
        fetch(`${API}/api/dashboard/run-health/routing?${qs}`, opts).then(r => r.json()),
        fetch(`${API}/api/dashboard/run-health/timeline?${qs}`, opts).then(r => r.json()),
        fetch(`${API}/api/dashboard/run-health/recent`, opts).then(r => r.json()),
        fetch(`${API}/api/alerts/summary`, opts).then(r => r.json()).catch(() => null),
      ]);
      setOverview(ov);
      setProviders(pv);
      setGovernance(gv);
      setRouting(rv);
      setTimeline(tv);
      setRecent(rc);
      setAlertSummary(al);
    } catch (e) {
      console.error('Dashboard fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--bg-primary)]">
        <CircleNotch size={24} className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  const ov = overview || {};
  const prov = providers?.providers || [];
  const gov = governance || {};
  const rt = routing || {};
  const tl = timeline?.data || [];
  const feed = recent?.items || [];

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]" data-testid="run-health-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[var(--bg-primary)] border-b border-[var(--border-subtle)] px-6 py-3">
        <div className="flex items-center justify-between max-w-[1400px] mx-auto">
          <div className="flex items-center gap-3">
            <Heartbeat size={18} weight="fill" className="text-[var(--brand-primary)]" />
            <h1 className="text-sm font-semibold text-[var(--text-primary)]">Run Health</h1>
          </div>
          <div className="flex items-center gap-3">
            <TimeRangePicker value={timeRange} onChange={setTimeRange} />
            <button onClick={fetchAll} className="p-1.5 rounded-lg hover:bg-[var(--surface-2)] transition-colors" data-testid="refresh-btn">
              <ArrowsClockwise size={14} className="text-[var(--text-muted)]" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-5 space-y-5">
        {/* ── Core Metrics ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="core-metrics">
          <MetricCard label="Total Runs" value={ov.total_runs || 0} icon={Lightning} color={C.blue} testId="metric-total-runs" />
          <MetricCard label="Completed" value={ov.completed || 0} sub={`${ov.success_rate || 0}%`} icon={CheckCircle} color={C.green} testId="metric-completed" />
          <MetricCard label="Failed" value={ov.failed || 0} sub={`${ov.failure_rate || 0}%`} icon={WarningCircle} color={C.red} testId="metric-failed" />
          <MetricCard label="Blocked" value={ov.blocked_by_governance || 0} icon={Shield} color={C.amber} testId="metric-blocked" />
          <MetricCard label="Avg Duration" value={`${ov.avg_duration_ms || 0}ms`} icon={Clock} color={C.teal} testId="metric-avg-duration" />
          <MetricCard label="Credits Used" value={ov.total_credits_used || 0} icon={Database} color={C.violet} testId="metric-credits" />
          <MetricCard label="Fallback Rate" value={`${ov.fallback_usage_rate || 0}%`} sub={`${ov.fallback_usage_count || 0} runs`} icon={ArrowsClockwise} color={C.cyan} testId="metric-fallback" />
        </div>

        {/* ── Alert Summary (if any open events) ── */}
        {alertSummary && (alertSummary.open_events > 0 || alertSummary.critical_events > 0) && (
          <div className="p-4 rounded-xl border bg-[var(--surface-1)]" style={{ borderColor: alertSummary.critical_events > 0 ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)' }} data-testid="alert-banner">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: alertSummary.critical_events > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)' }}>
                <WarningCircle size={16} weight="fill" style={{ color: alertSummary.critical_events > 0 ? C.red : C.amber }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    {alertSummary.open_events} Open Alert{alertSummary.open_events !== 1 ? 's' : ''}
                  </span>
                  {alertSummary.critical_events > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'rgba(239,68,68,0.1)', color: C.red }}>
                      {alertSummary.critical_events} Critical
                    </span>
                  )}
                </div>
                {alertSummary.recent_events?.length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {alertSummary.recent_events.slice(0, 3).map((e, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: e.severity === 'critical' ? C.red : C.amber }} />
                        <span className="text-[var(--text-secondary)] truncate">{e.alert_name}</span>
                        <span className="text-[var(--text-muted)] ml-auto shrink-0">{e.message?.slice(0, 60)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-[11px] text-[var(--text-muted)]">{alertSummary.active_alerts} rules active</span>
            </div>
          </div>
        )}

        {/* ── Timeline + Status Split ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Section title="Run Activity" icon={TrendUp} testId="section-timeline">
            {tl.length > 0 ? (
              <div>
                <Sparkline data={tl} height={48} color={C.blue} />
                <div className="flex justify-between mt-2">
                  <span className="text-[10px] text-[var(--text-muted)]">{tl[0]?.timestamp}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">{tl[tl.length - 1]?.timestamp}</span>
                </div>
              </div>
            ) : <p className="text-xs text-[var(--text-muted)] text-center py-3">No data for this period</p>}
          </Section>

          <Section title="Status Distribution" icon={Funnel} testId="section-status-dist">
            <div className="flex items-center gap-5">
              <DonutChart segments={[
                { value: ov.completed || 0, color: C.green },
                { value: ov.failed || 0, color: C.red },
                { value: ov.blocked_by_governance || 0, color: C.amber },
                { value: ov.running || 0, color: C.blue },
                { value: ov.cancelled || 0, color: C.gray },
              ]} />
              <div className="space-y-1.5">
                {[
                  { label: 'Completed', val: ov.completed, color: C.green },
                  { label: 'Failed', val: ov.failed, color: C.red },
                  { label: 'Blocked', val: ov.blocked_by_governance, color: C.amber },
                  { label: 'Running', val: ov.running, color: C.blue },
                ].filter(s => s.val > 0).map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
                    <span className="text-[11px] text-[var(--text-secondary)]">{s.label}</span>
                    <span className="text-[11px] font-mono text-[var(--text-primary)]">{s.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          <Section title="Internal vs External" icon={Robot} testId="section-int-ext">
            <div className="flex items-center gap-5">
              <DonutChart segments={[
                { value: ov.internal_runs || 0, color: C.blue },
                { value: ov.external_runs || 0, color: C.teal },
              ]} size={72} />
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: C.blue }} />
                  <span className="text-[11px] text-[var(--text-secondary)]">Internal</span>
                  <span className="text-[11px] font-mono text-[var(--text-primary)]">{ov.internal_runs || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: C.teal }} />
                  <span className="text-[11px] text-[var(--text-secondary)]">External</span>
                  <span className="text-[11px] font-mono text-[var(--text-primary)]">{ov.external_runs || 0}</span>
                </div>
              </div>
            </div>
          </Section>
        </div>

        {/* ── Provider + Governance + Routing ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Provider Insights */}
          <Section title="Top Providers" icon={Lightning} testId="section-providers">
            {prov.length > 0 ? (
              <BarChart
                data={prov.slice(0, 8).map(p => ({
                  label: p.provider_id?.replace(/^(fed_|prov_|system_)/, '') || 'unknown',
                  value: p.total_runs,
                  success_rate: p.success_rate,
                  provider_type: p.provider_type,
                }))}
                colorFn={(d) => d.provider_type === 'external_agent' ? C.teal : C.blue}
                maxVal={null}
              />
            ) : <p className="text-xs text-[var(--text-muted)] text-center py-3">No provider data</p>}
          </Section>

          {/* Governance Insights */}
          <Section title="Governance" icon={Shield} testId="section-governance">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-[var(--text-primary)]">{gov.total_blocked || 0}</span>
                <span className="text-[11px] text-[var(--text-muted)]">blocked executions</span>
              </div>
              {gov.top_blocked_reasons?.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Top Block Reasons</p>
                  {gov.top_blocked_reasons.slice(0, 5).map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                      <CaretRight size={8} className="text-[var(--text-muted)]" />
                      <span className="text-[var(--text-secondary)] truncate">{r.reason}</span>
                      <span className="ml-auto font-mono text-[var(--text-muted)]">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
              {gov.trust_class_distribution?.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Trust Distribution</p>
                  <BarChart
                    data={gov.trust_class_distribution.slice(0, 5).map(t => ({
                      label: (t.trust_class || 'none').replace(/_/g, ' '),
                      value: t.count,
                    }))}
                    colorFn={() => C.amber}
                  />
                </div>
              )}
            </div>
          </Section>

          {/* Routing Insights */}
          <Section title="Routing" icon={TrendUp} testId="section-routing">
            <div className="space-y-3">
              {rt.top_capabilities?.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Top Capabilities</p>
                  <BarChart
                    data={rt.top_capabilities.slice(0, 6).map(c => ({
                      label: c.capability_id?.replace(/_/g, ' ') || 'unknown',
                      value: c.total,
                    }))}
                    colorFn={() => C.violet}
                  />
                </div>
              )}
              {rt.routing_statuses?.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Routing Status</p>
                  {rt.routing_statuses.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: s.status === 'resolved' ? C.green : s.status === 'blocked' ? C.red : C.gray }} />
                      <span className="text-[var(--text-secondary)]">{s.status?.replace(/_/g, ' ')}</span>
                      <span className="ml-auto font-mono text-[var(--text-muted)]">{s.count}</span>
                    </div>
                  ))}
                </div>
              )}
              {rt.fallback_chain_frequency?.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Fallback Chains</p>
                  {rt.fallback_chain_frequency.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="text-[var(--text-secondary)]">{f.chain_length} provider{f.chain_length > 1 ? 's' : ''}</span>
                      <span className="ml-auto font-mono text-[var(--text-muted)]">{f.count} runs</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Section>
        </div>

        {/* ── Recent Activity Feed ── */}
        <Section title="Recent Activity" icon={Clock} testId="section-recent">
          <ActivityFeed items={feed} />
        </Section>
      </div>
    </div>
  );
}
